import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    IconButton,
    Modal,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Alert,
    CircularProgress
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import modalStyle from "../../styles/modalStyle";
import { FieldAlias, FieldSecurity, FieldSecurityRequest } from "../../interfaces/field";
import apiClient from "../../axiosClient";

interface ConfigureFieldSecurityModalProps {
    open: boolean;
    onClose: () => void;
    selectedFieldAlias: FieldAlias; // Single field alias only
    onSave: () => void;
}


const ConfigureFieldSecurityModal: React.FC<ConfigureFieldSecurityModalProps> = ({
    open,
    onClose,
    selectedFieldAlias,
    onSave: onSaveCallback
}) => {
    const [permissions, setPermissions] = useState<FieldSecurityRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Load existing permissions when modal opens
    useEffect(() => {
        if (open && selectedFieldAlias) {
            loadPermissions();
        }
    }, [open, selectedFieldAlias]);

    const loadPermissions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/api/v1/field_security/${selectedFieldAlias.field_alias_id}`);
            const existingPermissions: FieldSecurity[] = response.data;
            
            // Convert to FieldSecurityRequest format
            const permissionRequests = existingPermissions.map(perm => ({
                field_security_id: perm.field_security_id,
                field_alias_id: perm.field_alias_id,
                owner_type: perm.owner_type,
                user_role: perm.user_role,
                read_permission: perm.read_permission,
                write_permission: perm.write_permission,
                download_permission: perm.download_permission
            }));
            
            setPermissions(permissionRequests);
        } catch (error) {
            console.error('Error loading permissions:', error);
            setError('Failed to load existing permissions');
            setPermissions([]); // Show empty state if loading fails
        } finally {
            setLoading(false);
        }
    };

    const updatePermission = (index: number, field: keyof FieldSecurityRequest, value: any) => {
        setPermissions(prev => prev.map((perm, i) => 
            i === index ? { ...perm, [field]: value } : perm
        ));
    };

    const savePermissions = async () => {
        setSaving(true);
        setError(null);
        try {
            // Save permissions for the single selected field alias
            const response = await apiClient.put('/api/v1/field_security', permissions);
            if (response.status === 200) {
                onSaveCallback();
                onClose();
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to save permissions');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setPermissions([]);
        setError(null);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Configure Field Access Permissions
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Selected Field Info */}
                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                        Field Alias Information
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="body2"><strong>Alias Name:</strong> {selectedFieldAlias.alias_name}</Typography>
                        <Typography variant="body2"><strong>Field Name:</strong> {selectedFieldAlias.field_name}</Typography>
                        <Typography variant="body2"><strong>Client:</strong> {selectedFieldAlias.client_name}</Typography>
                        <Typography variant="body2"><strong>Field Type:</strong> {selectedFieldAlias.field_type}</Typography>
                    </Paper>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {permissions.length === 0 ? (
                            <Box textAlign="center" py={4}>
                                <Typography variant="body2" color="textSecondary">
                                    No field security permissions found. Please create permissions first.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="subtitle2" mb={2}>
                                    Permission Configuration
                                </Typography>
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Owner Type</TableCell>
                                            <TableCell>User Role</TableCell>
                                            <TableCell align="center">Read</TableCell>
                                            <TableCell align="center">Write</TableCell>
                                            <TableCell align="center">Download</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {permissions.map((permission, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {permission.owner_type}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {permission.user_role}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={permission.read_permission}
                                                        onChange={(e) => updatePermission(index, 'read_permission', e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={permission.write_permission}
                                                        onChange={(e) => updatePermission(index, 'write_permission', e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={permission.download_permission}
                                                        onChange={(e) => updatePermission(index, 'download_permission', e.target.checked)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            </>
                        )}
                    </>
                )}

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="outlined" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={savePermissions}
                        disabled={saving || loading || permissions.length === 0}
                    >
                        {saving ? <CircularProgress size={20} /> : 'Save Permissions'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ConfigureFieldSecurityModal;